import axios from 'axios'
import {
	Message
} from 'element-ui'

// 创建axios实例
const service = axios.create({
	baseURL: process.env.VUE_APP_API_BASE_URL,
	timeout: 30000,
	// 参数序列化器：跳过 null / undefined / 空字符串，避免把 "null"、"undefined" 发送到后端
	paramsSerializer: function(params) {
		const searchParams = new URLSearchParams();

		for (const key in params) {
			if (!params.hasOwnProperty(key)) continue;
			const value = params[key];
			// 空值不参与序列化，交给后端使用参数默认值（如 bool? isActive = null）
			if (value === null || value === undefined || value === '') continue;
			searchParams.append(key, value);
		}

		return searchParams.toString();
	}
})

// 请求拦截器
service.interceptors.request.use(
	config => {
		const token = localStorage.getItem('token')
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	},
	error => {
		return Promise.reject(error)
	}
)

// 处理ASP.NET Core序列化中的$符号字段
function normalizeAspDotNetFields(obj) {
	if (!obj || typeof obj !== 'object') return obj;

	if (Array.isArray(obj)) {
		return obj.map(item => normalizeAspDotNetFields(item));
	}

	const result = {};

	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			// 处理$符号开头的字段名
			let normalizedKey = key;
			if (key.startsWith('$')) {
				// 移除$前缀，但保留常见的特殊字段处理
				if (key === '$id' || key === '$ref') {
					// 这些是ASP.NET Core的引用字段，通常不需要在前端使用
					continue;
				} else if (key === '$values') {
					// $values字段通常包含实际的数组数据
					return normalizeAspDotNetFields(obj[key]);
				} else {
					normalizedKey = key.substring(1);
				}
			}

			// 递归处理嵌套对象
			result[normalizedKey] = normalizeAspDotNetFields(obj[key]);
		}
	}

	return result;
}

// 响应拦截器
service.interceptors.response.use(
	response => {
		const res = response.data
		// 兼容大小写不同的字段名（Success/success, Data/data, Message/message）
		const successField = res.hasOwnProperty('Success') ? 'Success' : 'success'
		const dataField = res.hasOwnProperty('Data') ? 'Data' : 'data'
		const messageField = res.hasOwnProperty('Message') ? 'Message' : 'message'

		// 规范化ASP.NET Core的$符号字段
		const normalizedRes = normalizeAspDotNetFields(res);

		// 确保返回统一的响应格式结构
		// 对于会员积分相关API，保留完整的响应结构
		if (response.config.url.includes('/MemberPoints')) {
			// 返回完整的响应结构，包含Success、Data、Message字段
			return {
				Success: normalizedRes[successField] || normalizedRes[successField.toLowerCase()] || false,
				Data: normalizedRes[dataField] || normalizedRes[dataField.toLowerCase()] || null,
				Message: normalizedRes[messageField] || normalizedRes[messageField.toLowerCase()] || ''
			};
		}

		// 对于其他API保持原有逻辑
		if (res[successField]) {
			const data = res[dataField];
			const normalizedData = normalizeAspDotNetFields(data);

			// 对于会员列表API，返回Data字段的内容
			if (response.config.url.includes('/members') && response.config.method === 'get') {
				return normalizedData;
			}

			// 对于菜单列表、用户定位和药物API，保留完整的分页结构
			const isMenuApi = response.config.url.includes('/menus') && !response.config.url.includes('/menus/');
			const isUserLocationApi = response.config.url.includes('/UserLocations/getUserLocations');
			const isDrugApi = response.config.url.includes('/drugarticles') && !response.config.url.includes('/drugarticles/');
			if ((isMenuApi || isUserLocationApi || isDrugApi) && response.config.method === 'get') {
				// 处理用户定位API的特殊数据结构
				if (isUserLocationApi) {
					// 用户定位API返回的数据结构：{ data: { list: [...], total: 100 } }
					if (normalizedData && normalizedData.list && normalizedData.total !== undefined) {
						return {
							data: normalizedData.list,
							total: normalizedData.total,
							page: normalizedData.pageIndex || parseInt(response.config.params?.page) || 1,
							pageSize: normalizedData.pageSize || parseInt(response.config.params?.pageSize) || 10
						};
					}
				}
				// 处理菜单API的常规分页结构
				// 检查原始响应中是否包含total字段（后端返回的分页数据）
				if (normalizedRes.total !== undefined || normalizedRes.Total !== undefined) {
					return {
						success: normalizedRes[successField] || true,
						data: normalizedRes[dataField] || [],
						total: normalizedRes.total !== undefined ? normalizedRes.total : normalizedRes.Total,
						page: normalizedRes.page !== undefined ? normalizedRes.page : (normalizedRes.Page ||
							parseInt(response.config.params?.pageIndex) || parseInt(response.config.params?.page) || 1),
						pageSize: normalizedRes.pageSize !== undefined ? normalizedRes.pageSize : (normalizedRes
							.PageSize || parseInt(response.config.params?.pageSize) || 10)
					};
				}
				// 如果normalizedData已经是分页结构，直接返回
				if (normalizedData && (normalizedData.items || normalizedData.data) && normalizedData.total !==
					undefined) {
					return normalizedData;
				}
				// 如果是数组，包装成分页结构
				if (Array.isArray(normalizedData)) {
					return {
						data: normalizedData,
						total: normalizedData.length,
						page: parseInt(response.config.params?.pageIndex) || parseInt(response.config.params?.page) || 1,
						pageSize: parseInt(response.config.params?.pageSize) || 10
					};
				}
			}

			// 对于修改密码等没有Data字段的API，返回完整的响应结构
			if (normalizedData === undefined) {
				return {
					Success: normalizedRes[successField] || true,
					Message: normalizedRes[messageField] || '',
					Data: null
				};
			}

			return normalizedData;
		} else {
			Message.error(res[messageField] || '请求失败')
			return Promise.reject(new Error(res[messageField] || '请求失败'))
		}
	},
	error => {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					Message.error('未授权，请重新登录')
					localStorage.removeItem('token')
					window.location.href = '/login'
					break
				case 403:
					Message.error('拒绝访问')
					break
				case 404:
					Message.error('请求资源不存在')
					break
				case 500:
					Message.error('服务器内部错误')
					break
				default:
					Message.error(error.response.data?.message || '请求失败')
			}
		} else {
			Message.error('网络错误，请检查网络连接')
		}
		return Promise.reject(error)
	}
)

// 文件上传API
// 注意：后端 UploadsController 的 Upload 方法要求 int userId，必须以查询参数携带，否则返回"缺少有效的用户ID"
export const uploadAPI = {
	// 获取当前登录用户ID（写入 localStorage 的登录信息中）
	_currentUserId() {
		try {
			const userStr = localStorage.getItem('user') || localStorage.getItem('userInfo')
			if (userStr) {
				const user = JSON.parse(userStr)
				return user.id || user.Id || user.userId || user.UserId || 0
			}
		} catch (e) {
			// 忽略解析错误
		}
		return 0
	},

	// 从上传接口响应中提取相对 url（兼容 {Url}/{url} 字段及拦截器归一化前后的形态）
	_extractUrl(res) {
		if (!res) return ''
		// 拦截器已把后端 {Success, Data} 归一化为 Data 内容时
		if (res.Url || res.url) return res.Url || res.url
		if (res.Data && (res.Data.Url || res.Data.url)) return res.Data.Url || res.Data.url
		return ''
	},

	// 上传单个图片文件到 uploads/images 目录
	uploadImage: (file, userId) => {
		const formData = new FormData();
		formData.append('file', file);
		const uid = userId !== undefined ? userId : uploadAPI._currentUserId();
		return service.post('/uploads/image', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			params: {
				userId: uid
			},
			timeout: 300000
		}).then(r => uploadAPI._extractUrl(r));
	},

	// 上传单个视频文件到 uploads/videos 目录
	uploadVideo: (file, userId) => {
		const formData = new FormData();
		formData.append('file', file);
		const uid = userId !== undefined ? userId : uploadAPI._currentUserId();
		return service.post('/uploads/video', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			params: {
				userId: uid
			},
			timeout: 300000
		}).then(r => uploadAPI._extractUrl(r));
	},

	// 便捷封装：根据文件类型自动选择图片/视频上传接口，返回相对 url
	// file: 单个 File 对象
	uploadMedia: (file, userId) => {
		const isVideoFile = /\.(mp4|mov|avi|mkv|webm|flv|wmv)$/i.test(file.name) ||
			(file.type && file.type.startsWith('video/'));
		return isVideoFile ? uploadAPI.uploadVideo(file, userId) : uploadAPI.uploadImage(file, userId);
	},

	// 获取视频列表
	getVideos: (params) => service.get('/uploads/videos', {
		params
	}),

	// 删除视频
	deleteVideo: (id) => service.delete(`/uploads/video/${id}`)
};

// 认证相关API
export const authAPI = {
	// 登录
	login: (data) => service.post('/auth/login', data),

	// 验证token
	validateToken: (token) => service.post('/auth/validate', {
		token
	}),

	// 退出登录
	logout: (token) => service.post('/auth/logout', {
		token
	})
}

// 用户管理API
export const userAPI = {
	// 获取用户列表
	getUsers: (params) => service.get('/users', {
		params
	}),

	// 获取用户详情
	getUser: (id) => service.get(`/users/${id}`),

	// 创建用户
	createUser: (data) => service.post('/users', data),

	// 更新用户
	updateUser: (id, data) => service.put(`/users/${id}`, data),

	// 删除用户
	deleteUser: (id) => service.delete(`/users/${id}`),

	// 切换用户状态
	toggleUserStatus: (id) => service.patch(`/users/${id}/status`),

	// 修改密码
	changePassword: (userId, data) => service.post(`/users/${userId}/set-password`, data),

	// 修改其他用户密码
	changeOtherUserPassword: (userId, data) => service.post(`/users/${userId}/reset-password`, data)
}

// 角色管理API
export const roleAPI = {
	// 获取角色列表
	getRoles: (params) => service.get('/roles', {
		params
	}),

	// 获取角色列表,筛选
	GetAllRoles: (params) => service.get('/roles/all'),

	// 获取角色详情
	getRole: (id) => service.get(`/roles/${id}`),

	// 创建角色
	createRole: (data) => service.post('/roles', data),

	// 更新角色
	updateRole: (id, data) => service.put(`/roles/${id}`, data),

	// 删除角色
	deleteRole: (id) => service.delete(`/roles/${id}`),

	// 切换角色状态
	toggleRoleStatus: (id) => service.patch(`/roles/${id}/status`),

	// 获取角色权限
	getRolePermissions: (id) => service.get(`/roles/${id}/permissions`),

	// 设置角色权限
	setRolePermissions: (id, permissions) => service.put(`/roles/${id}/permissions`, {
		permissions
	})
}

// 菜单管理API
export const menuAPI = {
	// 获取菜单列表
	getMenus: (params) => service.get('/menus', {
		params
	}),

	// 获取菜单树
	getMenuTree: () => service.get('/menus/tree'),

	// 获取用户权限菜单
	getUserMenus: () => service.get('/menus/user'),

	// 获取菜单详情
	getMenu: (id) => service.get(`/menus/${id}`),

	// 创建菜单
	createMenu: (data) => service.post('/menus', data),

	// 更新菜单
	updateMenu: (id, data) => service.put(`/menus/${id}`, data),

	// 删除菜单
	deleteMenu: (id) => service.delete(`/menus/${id}`),

	// 切换菜单状态
	toggleMenuStatus: (id) => service.patch(`/menus/${id}/status`)
}

// 权限管理API
export const permissionAPI = {
	// 获取权限列表
	getPermissions: (params) => service.get('/permissions', {
		params
	}),

	// 获取权限详情
	getPermission: (id) => service.get(`/permissions/${id}`),

	// 创建权限
	createPermission: (data) => service.post('/permissions', data),

	// 更新权限
	updatePermission: (id, data) => service.put(`/permissions/${id}`, data),

	// 删除权限
	deletePermission: (id) => service.delete(`/permissions/${id}`),

	// 切换权限状态
	togglePermissionStatus: (id) => service.patch(`/permissions/${id}/status`)
}

// 系统统计API
export const statsAPI = {
	// 获取系统统计
	getSystemStats: () => service.get('/stats/system'),

	// 获取用户统计
	getUserStats: () => service.get('/stats/users'),

	// 获取角色统计
	getRoleStats: () => service.get('/stats/roles'),

	// 获取菜单统计
	getMenuStats: () => service.get('/stats/menus')
}

// 系统日志API
export const logAPI = {
	// 获取最近的系统日志
	getRecentLogs: (count = 10, uid) => service.get('/systemLogs/recent', {
		params: {
			count,
			uid
		}
	}),

	getAdminRecentLogs: (count = 10) => service.get('/systemLogs/adminrecent', {
		params: {
			count
		}
	}),

	// 获取系统日志列表
	getLogs: (page = 1, pageSize = 20, keyword = null, status = null) =>
		service.get('/systemLogs', {
			params: {
				page,
				pageSize,
				keyword,
				status
			}
		}),

	// 获取指定用户的系统日志
	getUserLogs: (userId, page = 1, pageSize = 10) =>
		service.get(`/systemLogs/user/${userId}`, {
			params: {
				page,
				pageSize
			}
		}),

	// 创建系统日志
	createLog: (data) => service.post('/systemLogs', data),

	// 兼容旧的日志API
	getOldLogs: (params) => service.get('/logs', {
		params
	}),
	getOldLog: (id) => service.get(`/logs/${id}`),
	createOldLog: (data) => service.post('/logs', data)
}

// 会员管理API
export const memberAPI = {
	// 获取会员列表
	getMembers: (params) => service.get('/members', {
		params
	}),

	// 获取会员详情
	getMember: (id) => service.get(`/members/${id}`),

	// 切换会员状态
	toggleMemberStatus: (id) => service.patch(`/members/${id}/status`),

	// 更新会员信息
	updateMember: (id, data) => service.put(`/members/${id}`, data),

	// 获取会员统计
	getMemberStatistics: () => service.get('/members/statistics')
}

// 系统配置API
export const systemConfigAPI = {
	// 获取所有配置
	getAllConfigs: () => service.get('/systemconfigs'),

	// 获取配置详情
	getConfig: (id) => service.get(`/systemconfigs/${id}`),

	// 根据键获取配置
	getConfigByKey: (key) => service.get(`/systemconfigs/key/${key}`),

	// 根据分组获取配置
	getConfigsByGroup: (group) => service.get(`/systemconfigs/group/${group}`),

	// 创建配置
	createConfig: (data) => service.post('/systemconfigs', data),

	// 更新配置
	updateConfig: (id, data) => service.put(`/systemconfigs/${id}`, data),

	// 删除配置
	deleteConfig: (id) => service.delete(`/systemconfigs/${id}`),

	// 获取微信配置
	getWechatConfig: () => service.get('/systemconfigs/wechat'),

	// 更新微信配置
	updateWechatConfig: (data) => service.put('/systemconfigs/wechat', data)
}

// 知识库相关API
export const knowledgeBaseAPI = {
	// 获取文章列表 - 支持分页和搜索参数
	getKnowledgeArticles: (page, pageSize, keyword, searchKeywords, categoryId, status, includeInactive =
		false) => {
		// 构建参数对象
		const params = {
			page: page || 1,
			pageSize: pageSize || 10,
			includeInactive
		};

		// 只有当参数有值时才添加到params对象中
		if (keyword) params.keyword = keyword;
		if (searchKeywords) params.searchKeywords = searchKeywords;
		if (categoryId) params.categoryId = categoryId;
		if (status) params.status = status;

		return service.get('/KnowledgeArticles', {
			params
		});
	},

	// 获取文章详情
	getKnowledgeArticle: (id, includeTags = false) =>
		service.get(`/KnowledgeArticles/${id}`, {
			params: {
				includeTags
			}
		}),

	// 创建文章 - 直接发送文章数据
	createKnowledgeArticle: (data) => service.post('/KnowledgeArticles', data),

	// 更新文章
	updateKnowledgeArticle: (id, data) => service.put(`/KnowledgeArticles/${id}`, data),

	// 删除文章
	deleteKnowledgeArticle: (id) => service.delete(`/KnowledgeArticles/${id}`),

	// 更新文章状态
	updateArticleStatus: (id, status) => service.patch(`/KnowledgeArticles/${id}/status`, status),

	// 搜索文章
	searchKnowledgeArticles: (keyword, categoryId = null) =>
		service.get('/KnowledgeArticles/search', {
			params: {
				keyword,
				categoryId
			}
		}),

	// 获取推荐文章
	getRecommendedArticles: (count = 10) =>
		service.get('/KnowledgeArticles/recommended', {
			params: {
				count
			}
		}),

	// 获取置顶文章
	getTopArticles: (count = 10) =>
		service.get('/KnowledgeArticles/top', {
			params: {
				count
			}
		}),

	// 获取最新文章
	getLatestArticles: (count = 10) =>
		service.get('/KnowledgeArticles/latest', {
			params: {
				count
			}
		}),

	// 按分类获取文章
	getArticlesByCategory: (categoryId, includeInactive = false) =>
		service.get(`/KnowledgeArticles/category/${categoryId}`, {
			params: {
				includeInactive
			}
		}),

	// 获取分类列表
	getKnowledgeCategories: () => service.get('/knowledgecategories'),

	// 获取分类详情
	getKnowledgeCategory: (id) => service.get(`/knowledgecategories/${id}`),

	// 获取分页分类列表
	getPagedKnowledgeCategories: (page = 1, pageSize = 10, keyword = '', status = null) => {
		// 构建参数对象，只包含非null/undefined/空字符串的值
		const params = {
			page,
			pageSize
		};
		// 只有当keyword不是空字符串、null或undefined时才添加到参数中
		if (keyword !== null && keyword !== undefined && keyword !== '') {
			params.keyword = keyword;
		}
		// 只有当status不是null或undefined时才添加到参数中
		if (status !== null && status !== undefined) {
			params.status = status;
		}
		return service.get('/knowledgecategories/paged', {
			params
		});
	},

	// 创建分类 - 调用后端CreateCategory接口
	createKnowledgeCategory: (data) => service.post('/knowledgecategories', data),

	// 更新分类 - 调用后端UpdateCategory接口
	updateKnowledgeCategory: (id, data) => service.put(`/knowledgecategories/${id}`, data),

	// 删除分类
	deleteKnowledgeCategory: (id) => service.delete(`/knowledgecategories/${id}`),

	// 获取分类数量统计
	getCategoriesCount: (includeInactive = false) =>
		service.get('/knowledgecategories/count', {
			params: {
				includeInactive
			}
		}),

	// 获取文章数量统计
	getArticlesCount: (categoryId = null, includeInactive = false) =>
		service.get('/knowledgearticles/count', {
			params: {
				categoryId,
				includeInactive
			}
		})
}

// 健康科普相关API
export const scienceAPI = {
	// ===== 文章 =====
	// 获取文章列表 - 支持分页和搜索参数
	getScienceArticles: (page, pageSize, keyword, searchKeywords, categoryId, status, includeInactive =
		false) => {
		const params = {
			page: page || 1,
			pageSize: pageSize || 10,
			includeInactive
		};
		if (keyword) params.keyword = keyword;
		if (searchKeywords) params.searchKeywords = searchKeywords;
		if (categoryId) params.categoryId = categoryId;
		if (status) params.status = status;
		return service.get('/ScienceArticles', {
			params
		});
	},

	getScienceArticle: (id) =>
		service.get(`/ScienceArticles/${id}`),

	createScienceArticle: (data) => service.post('/ScienceArticles', data),

	updateScienceArticle: (id, data) => service.put(`/ScienceArticles/${id}`, data),

	deleteScienceArticle: (id) => service.delete(`/ScienceArticles/${id}`),

	updateScienceArticleStatus: (id, status) => service.patch(`/ScienceArticles/${id}/status`, status),

	searchScienceArticles: (keyword, categoryId = null) =>
		service.get('/ScienceArticles/search', {
			params: {
				keyword,
				categoryId
			}
		}),

	getRecommendedScienceArticles: (count = 10) =>
		service.get('/ScienceArticles/recommended', {
			params: {
				count
			}
		}),

	getLatestScienceArticles: (count = 10) =>
		service.get('/ScienceArticles/latest', {
			params: {
				count
			}
		}),

	getScienceArticlesByCategory: (categoryId, includeInactive = false) =>
		service.get(`/ScienceArticles/category/${categoryId}`, {
			params: {
				includeInactive
			}
		}),

	getScienceArticlesCount: (categoryId = null, includeInactive = false) =>
		service.get('/ScienceArticles/count', {
			params: {
				categoryId,
				includeInactive
			}
		}),

	// ===== 分类 =====
	getScienceCategories: () => service.get('/ScienceCategories'),

	getScienceCategory: (id) => service.get(`/ScienceCategories/${id}`),

	getPagedScienceCategories: (page = 1, pageSize = 10, keyword = '', status = null) => {
		const params = {
			page,
			pageSize
		};
		if (keyword !== null && keyword !== undefined && keyword !== '') {
			params.keyword = keyword;
		}
		if (status !== null && status !== undefined) {
			params.status = status;
		}
		return service.get('/ScienceCategories/paged', {
			params
		});
	},

	createScienceCategory: (data) => service.post('/ScienceCategories', data),

	updateScienceCategory: (id, data) => service.put(`/ScienceCategories/${id}`, data),

	deleteScienceCategory: (id) => service.delete(`/ScienceCategories/${id}`),

	getScienceCategoriesCount: (includeInactive = false) =>
		service.get('/ScienceCategories/count', {
			params: {
				includeInactive
			}
		}),

	// ===== 视频 =====
	getScienceVideos: (page, pageSize, keyword, categoryId, status, includeInactive = false) => {
		const params = {
			page: page || 1,
			pageSize: pageSize || 10,
			includeInactive
		};
		if (keyword) params.keyword = keyword;
		if (categoryId) params.categoryId = categoryId;
		if (status) params.status = status;
		return service.get('/ScienceVideos', {
			params
		});
	},

	getScienceVideo: (id) => service.get(`/ScienceVideos/${id}`),

	getScienceVideosByCategory: (categoryId, includeInactive = false) =>
		service.get(`/ScienceVideos/category/${categoryId}`, {
			params: {
				includeInactive
			}
		}),

	getLatestScienceVideos: (count = 10) =>
		service.get('/ScienceVideos/latest', {
			params: {
				count
			}
		}),

	createScienceVideo: (data) => service.post('/ScienceVideos', data),

	updateScienceVideo: (id, data) => service.put(`/ScienceVideos/${id}`, data),

	deleteScienceVideo: (id) => service.delete(`/ScienceVideos/${id}`),

	getScienceVideosCount: (categoryId = null, includeInactive = false) =>
		service.get('/ScienceVideos/count', {
			params: {
				categoryId,
				includeInactive
			}
		})
}

// 产品管理API
export const productAPI = {
	// 获取产品列表
	getProducts: (params) => service.get('/products', {
		params
	}),

	// 获取激活产品列表（匿名访问）
	getActiveProducts: () => service.get('/products/active'),

	// 获取产品详情
	getProduct: (id) => service.get(`/products/${id}`),

	// 创建产品
	createProduct: (data) => service.post('/products', data),

	// 更新产品
	updateProduct: (id, data) => service.put(`/products/${id}`, data),

	// 删除产品
	deleteProduct: (id) => service.delete(`/products/${id}`),

	// 切换产品状态
	toggleProductStatus: (id) => service.patch(`/products/${id}/status`),

	// 获取产品统计
	getProductStatistics: () => service.get('/products/stats')
}

// 药物百科API
export const drugAPI = {
	// 获取药物列表
	getDrugArticles: (params) => service.get('/drugarticles', {
		params
	}),

	// 获取药物详情
	getDrugArticle: (id) => service.get(`/drugarticles/${id}`),

	// 创建药物
	createDrugArticle: (data) => service.post('/drugarticles', data),

	// 更新药物
	updateDrugArticle: (id, data) => service.put(`/drugarticles/${id}`, data),

	// 删除药物
	deleteDrugArticle: (id) => service.delete(`/drugarticles/${id}`),

	// 更新药物状态
	updateDrugStatus: (id, status) => service.patch(`/drugarticles/${id}/status`, status),

	// 搜索药物
	searchDrugArticles: (keyword, categoryId = null) =>
		service.get('/drugarticles/search', {
			params: {
				keyword,
				categoryId
			}
		}),

	// 获取推荐药物
	getRecommendedDrugs: (count = 10) =>
		service.get('/drugarticles/recommended', {
			params: {
				count
			}
		}),

	// 获取置顶药物
	getTopDrugs: (count = 10) =>
		service.get('/drugarticles/top', {
			params: {
				count
			}
		}),

	// 获取最新药物
	getLatestDrugs: (count = 10) =>
		service.get('/drugarticles/latest', {
			params: {
				count
			}
		}),

	// 按分类获取药物
	getDrugsByCategory: (categoryId, includeInactive = false) =>
		service.get(`/drugarticles/category/${categoryId}`, {
			params: {
				includeInactive
			}
		}),

	// 按剂型获取药物
	getDrugsByDosageForm: (dosageForm) =>
		service.get(`/drugarticles/dosage-form/${dosageForm}`),

	// 按生产厂家获取药物
	getDrugsByManufacturer: (manufacturer) =>
		service.get(`/drugarticles/manufacturer/${manufacturer}`),

	// 按适应症获取药物
	getDrugsByIndication: (indication) =>
		service.get(`/drugarticles/indication/${indication}`),

	// 获取分类列表
	getDrugCategories: () => service.get('/drugcategories'),

	// 获取分类详情
	getDrugCategory: (id) => service.get(`/drugcategories/${id}`),

	// 创建分类
	createDrugCategory: (data) => service.post('/drugcategories', data),

	// 更新分类
	updateDrugCategory: (id, data) => service.put(`/drugcategories/${id}`, data),

	// 删除分类
	deleteDrugCategory: (id) => service.delete(`/drugcategories/${id}`),

	// 获取分类数量统计
	getDrugCategoriesCount: (includeInactive = false) =>
		service.get('/drugcategories/count', {
			params: {
				includeInactive
			}
		}),

	// 获取药物数量统计
	getDrugsCount: (categoryId = null, includeInactive = false) =>
		service.get('/drugarticles/count', {
			params: {
				categoryId,
				includeInactive
			}
		})
}

// Banner管理API
export const bannerAPI = {
	// 获取banner列表
	getBanners: (params) => service.get('/Banners', {
		params
	}),

	// 获取激活的banner
	getActiveBanners: () => service.get('/Banners/active'),

	// 获取banner详情
	getBanner: (id) => service.get(`/Banners/${id}`),

	// 创建banner
	createBanner: (data) => service.post('/Banners', data),

	// 更新banner
	updateBanner: (id, data) => service.put(`/Banners/${id}`, data),

	// 删除banner
	deleteBanner: (id) => service.delete(`/Banners/${id}`),

	// 切换banner状态
	toggleBannerStatus: (id) => service.patch(`/Banners/${id}/status`)
}

// 公益掠影API
export const publicWelfareAPI = {
	// 获取列表
	getList: (params) => service.get('/PublicWelfares', {
		params
	}),

	// 获取详情
	getById: (id) => service.get(`/PublicWelfares/${id}`),

	// 创建
	create: (data) => service.post('/PublicWelfares', data),

	// 更新
	update: (id, data) => service.put(`/PublicWelfares/${id}`, data),

	// 删除
	delete: (id) => service.delete(`/PublicWelfares/${id}`),

	// 切换状态
	toggleStatus: (id) => service.put(`/PublicWelfares/${id}/status`)
}

// 记忆访谈API
export const memoryInterviewAPI = {
	// 获取列表
	getList: (params) => service.get('/MemoryInterviews', {
		params
	}),

	// 获取详情
	getById: (id) => service.get(`/MemoryInterviews/${id}`),

	// 创建
	create: (data) => service.post('/MemoryInterviews', data),

	// 更新
	update: (id, data) => service.put(`/MemoryInterviews/${id}`, data),

	// 删除
	delete: (id) => service.delete(`/MemoryInterviews/${id}`),

	// 切换状态
	toggleStatus: (id) => service.put(`/MemoryInterviews/${id}/status`)
}

// 内容点击量统计API
// contentType 取值：ScienceArticle / ScienceVideo / MemoryInterview / PublicWelfare
export const contentViewAPI = {
	// 按内容汇总点击量：GET /ContentViewRecords/stats?contentType=xxx
	getStats: (contentType) => service.get(`/ContentViewRecords/stats?contentType=${encodeURIComponent(contentType)}`),

	// 单个内容点击次数：GET /ContentViewRecords/count?contentType=xxx&contentId=1
	getCount: (contentType, contentId) => service.get(
		`/ContentViewRecords/count?contentType=${encodeURIComponent(contentType)}&contentId=${contentId}`),

	// 按天统计：GET /ContentViewRecords/daily?contentType=xxx&contentId=1&days=7
	getDailyStats: (contentType, contentId, days = 7) => service.get(
		`/ContentViewRecords/daily?contentType=${encodeURIComponent(contentType)}&contentId=${contentId}&days=${days}`),

	// 上报一次点击：POST /ContentViewRecords
	record: (contentType, contentId, userId = null) => service.post('/ContentViewRecords', {
		contentType,
		contentId,
		userId,
		clientType: 'admin'
	})
}

// 企业信息API
export const companyInfoAPI = {
	// 获取企业简介配置
	getCompanyProfiles: () => service.get('/systemconfigs/group/CompanyProfile'),

	// 获取指定的企业简介配置
	getCompanyProfileByKey: (key) => service.get(`/systemconfigs/key/${key}`),

	// 更新企业简介配置
	updateCompanyProfile: (id, data) => service.put(`/systemconfigs/${id}`, data)
}

// 家人关系API
export const familyRelationAPI = {
	// 获取家人关系列表
	getFamilyRelations: (userId) => service.get(`/userfamilyrelations/${userId}`),

	// 获取家人关系详情
	getFamilyRelation: (userId, relationId) => service.get(`/userfamilyrelations/${userId}/${relationId}`),

	// 创建家人关系
	createFamilyRelation: (userId, data) => service.post(`/userfamilyrelations/${userId}`, data),

	// 更新家人关系
	updateFamilyRelation: (userId, relationId, data) => service.put(`/userfamilyrelations/${userId}/${relationId}`,
		data),

	// 激活家人关系
	activateFamilyRelation: (data) => service.post('/userfamilyrelations/activate', data),

	// 删除家人关系
	deleteFamilyRelation: (userId, relationId) => service.delete(`/userfamilyrelations/${userId}/${relationId}`)
}

// 系统协议相关API
export const systemProtocolsAPI = {
	// 获取隐私设置
	getPrivacyPolicy: () => service.get('/SystemProtocols/privacy'),
	// 更新隐私设置
	updatePrivacyPolicy: (data) => service.put('/SystemProtocols/privacy', data),
	// 获取保护协议
	getProtectionProtocol: () => service.get('/SystemProtocols/protection'),
	// 更新保护协议
	updateProtectionProtocol: (data) => service.put('/SystemProtocols/protection', data)
}

// 服务页面管理API
export const servicePageAPI = {
	// 获取当前激活的配置
	getActiveConfig: () => service.get('/ServicePageConfigs'),
	// 创建或更新配置
	createOrUpdateConfig: (data) => service.post('/ServicePageConfigs', data),
	// 检查配置是否存在
	configExists: () => service.get('/ServicePageConfigs/exists'),
	// 获取FAQ列表
	getFaqs: (params) => service.get('/ServiceFaqs', {
		params
	}),
	// 获取激活的FAQ列表
	getActiveFaqs: () => service.get('/ServiceFaqs/active'),
	// 获取单个FAQ
	getFaq: (id) => service.get(`/ServiceFaqs/${id}`),
	// 创建FAQ
	createFaq: (data) => service.post('/ServiceFaqs', data),
	// 更新FAQ
	updateFaq: (id, data) => service.put(`/ServiceFaqs/${id}`, data),
	// 删除FAQ
	deleteFaq: (id) => service.delete(`/ServiceFaqs/${id}`),
	// 切换FAQ状态
	toggleFaqStatus: (id) => service.put(`/ServiceFaqs/${id}/status`),
	// 获取FAQ统计信息
	getFaqStats: () => service.get('/ServiceFaqs/stats')
}

// 导出所有API服务
// familyContactService 已移除，因为文件不存在

// 紧急联系人相关API
export const emergencyContactAPI = {
	// 获取指定用户的紧急联系人列表
	getByUserId: (userId) => {
		return service.get(`/EmergencyContact/user/${userId}`);
	},

	// 分页获取紧急联系人列表
	getPagedContacts: (page, pageSize, contactName, phone) => {
		// 构建参数对象，参考knowledgeBaseAPI的参数处理方式
		const params = {
			page: page || 1,
			pageSize: pageSize || 10
		};

		// 只有当参数有值时才添加到params对象中
		if (contactName) params.contactName = contactName;
		if (phone) params.phone = phone;

		return service.get('/EmergencyContact/paged', {
			params
		});
	},

	// 获取当前用户的紧急联系人列表
	getCurrentUserContacts: () => {
		return service.post('/EmergencyContact/current');
	},

	// 获取单个紧急联系人详情
	getById: (id) => {
		return service.get(`/EmergencyContact/${id}`);
	},

	// 创建紧急联系人
	create: (data) => {
		// 从查询参数传递userId
		const params = {
			userId: data.userId
		};
		return service.post('/EmergencyContact', {
			ContactName: data.contactName,
			ContactPhone: data.phone,
			Relationship: data.relationship
		}, {
			params
		});
	},

	// 更新紧急联系人
	update: (data) => {
		return service.put(`/EmergencyContact/${data.id}`, {
			ContactName: data.contactName,
			ContactPhone: data.phone,
			Relationship: data.relationship,
			Remark: data.remark
		});
	},

	// 删除紧急联系人（软删除）
	delete: (id) => {
		return service.delete(`/EmergencyContact/${id}`);
	}
}

// AD认知筛查提交API
export const adScreeningSubmissionAPI = {
	// 获取AD筛查提交列表
	getSubmissions: (params) => service.get('/AdScreeningSubmissions', { params }),
	
	// 获取AD筛查提交详情
	getSubmission: (id) => service.get(`/AdScreeningSubmissions/${id}`),
	
	// 创建AD筛查提交
	createSubmission: (data) => service.post('/AdScreeningSubmissions', data),
	
	// 更新AD筛查提交
	updateSubmission: (id, data) => service.put(`/AdScreeningSubmissions/${id}`, data),
	
	// 删除AD筛查提交
	deleteSubmission: (id) => service.delete(`/AdScreeningSubmissions/${id}`)
};

// 用户定位管理API
export const userLocationAPI = {
	// 获取用户定位列表
	getAllUserLocations: (params) => {
		// 构建参数对象
		const queryParams = {
			page: params.pageIndex || params.page || 1,
			pageSize: params.pageSize || 10
		};

		// 只有当参数有值时才添加到params对象中
		if (params.userId) queryParams.userId = params.userId;
		if (params.startTime) queryParams.startDate = params.startTime;
		if (params.endTime) queryParams.endDate = params.endTime;
		if (params.location) queryParams.location = params.location;

		return service.get('/UserLocations/getUserLocations', {
			params: queryParams
		});
	}
};

// 照护机构管理API
export const careInstitutionsAPI = {
	// 获取照护机构列表
	getCareInstitutions: (page, pageSize, keyword, regionId, type, isActive) => {
		// 构建参数对象
		const queryParams = {
			pageIndex: page || 1,
			pageSize: pageSize || 10
		};

		// 只有当参数有值时才添加到params对象中
		if (keyword) queryParams.keyword = keyword;
		if (regionId) queryParams.regionId = regionId;
		if (type) queryParams.type = type;
		if (isActive !== undefined) queryParams.isActive = isActive;

		return service.get('/careinstitutions', {
			params: queryParams
		});
	},

	// 获取单个照护机构详情
	getCareInstitution: (id) => {
		return service.get(`/careinstitutions/${id}`);
	},

	// 创建新的照护机构
	createCareInstitution: (data) => {
		return service.post('/careinstitutions', data);
	},

	// 更新照护机构信息
	updateCareInstitution: (data) => {
		return service.put(`/careinstitutions/${data.Id}`, data);
	},

	// 删除照护机构
	deleteCareInstitution: (id) => {
		return service.delete(`/careinstitutions/${id}`);
	}
};

export default service